/**
 * messagingAccess — geteilte Sichtbarkeits-Logik für die Owner ↔ Steuerberater
 * Messaging-Plattform (M-MSG-3/4).
 *
 * Single source of truth dafür, OB Messaging (Nav-Eintrag, Route, Kontext-Icons,
 * Hooks-Mount) sichtbar/aktiv ist. Wird von `MainLayout` (Nav-Eintrag),
 * `MessageContextButton` (Kontext-Icons) und `MessagesPage` (Content-Gate)
 * gemeinsam genutzt — kein Copy-Paste der Zugriffs-Bedingung.
 *
 * Zwei Freischalt-Pfade (fail-closed — Default ist KEIN Zugriff), analog
 * `receiptScanAccess`/`advisorInviteAccess`:
 *   1. Beta-Tenant-Allowlist: der Test-Tenant sieht Messaging LIVE, unabhängig
 *      vom Env-Flag (kontrollierte Erprobung).
 *   2. Allgemeine Freigabe: `REACT_APP_MESSAGING_ENABLED==='true'`.
 *
 * Rollenunabhängig — bewusst KEINE `isReadOnly`/`isAdvisorViewingClient`-
 * Verknüpfung hier, weil Messaging für BEIDE Rollen (owner UND advisor)
 * sichtbar sein muss (§9.2/§9.3). Diese Datei kennt daher gar keine Rolle —
 * jede rollenabhängige Kuratierung bliebe ein Bug, wenn sie hier landen würde.
 *
 * `canUseMessaging` steuert (architecture-messaging-platform.md §6, erweitert
 * um die Beta-Allowlist):
 *   - Nav-Eintrag „Nachrichten" (Desktop + Mobile) in `MainLayout`.
 *   - Die Kontext-Icons (`MessageContextButton`) an Rechnungs-/Beleg-Zeilen.
 *   - Ob `useMessages`/`useUnreadCount` gemountet werden (nur innerhalb
 *     `MessageThreadPanel`/`MessageContextButton`, die selbst nur bei
 *     `canUseMessaging(effectiveScopeUid)===true` rendern).
 *
 * Die Route `/dashboard/nachrichten` selbst bleibt registriert (kein Gate auf
 * Route-Ebene) — ohne Nav-Einstiegspunkt ist sie ohne Zugriff praktisch
 * unerreichbar; das entspricht dem im Auftrag vorgegebenen Verhalten
 * ("MessagesPage-Route existiert zwar, aber ohne Einstiegspunkt").
 *
 * @module messagingAccess
 */

/**
 * Beta-Erprobung Messaging — entfernen/erweitern, wenn allgemein freigegeben.
 *
 * Nur dieser Tenant-Scope (`effectiveScopeUid`) erhält Zugriff unabhängig vom
 * Env-Flag. `b3DRR1Cj63cRjdJg5VCEUpBL7hk1` = Test-Account daniel_eich@outlook.com
 * (identisch zu `RECEIPT_SCAN_BETA_UIDS`/`ADVISOR_INVITE_BETA_UIDS`).
 * HARTKODIERT für die kontrollierte Live-Erprobung; vor allgemeiner Freigabe
 * (Flag = 'true' für alle) wieder leeren.
 */
export const MESSAGING_BETA_TENANT_UIDS: readonly string[] = ['b3DRR1Cj63cRjdJg5VCEUpBL7hk1'];

/**
 * Allgemeines Feature-Flag für die Messaging-Plattform. Fail-closed: nur bei
 * explizitem `'true'` aktiv. Modul-Konstante, einmalig beim Bundle-Load
 * ausgewertet. Bleibt der spätere Allgemein-Freigabe-Pfad — bis dahin treibt
 * die Beta-Allowlist die Sichtbarkeit (siehe {@link canUseMessaging}).
 */
export const MESSAGING_ENABLED: boolean = process.env.REACT_APP_MESSAGING_ENABLED === 'true';

/**
 * Entscheidet fail-closed, ob Messaging für einen Tenant-Scope sichtbar ist.
 *
 * WICHTIG: gated auf `effectiveScopeUid` (den Tenant-SCOPE), NICHT auf
 * `currentUser.uid`. Grund: Messaging muss für BEIDE Rollen im Test-Tenant
 * sichtbar sein — den Owner (dessen `effectiveScopeUid` die eigene UID ist)
 * UND einen Steuerberater, der den Test-Tenant betrachtet (dessen
 * `effectiveScopeUid` auf die UID des Tenant-Owners aufgelöst wird). Ein Gate
 * auf `currentUser.uid` würde den Advisor-Fall verpassen. Rein kosmetisches
 * Client-Gating — die harte Sperre bleiben die Firestore-Rules.
 *
 * @param scopeUid - `effectiveScopeUid` aus `useAuth()` (oder null/undefined,
 *                   solange noch nicht aufgelöst).
 * @returns `true`, wenn `scopeUid` auf der Beta-Tenant-Allowlist steht ODER
 *          das allgemeine Flag aktiv ist. Sonst `false`.
 */
export function canUseMessaging(scopeUid: string | null | undefined): boolean {
  return (!!scopeUid && MESSAGING_BETA_TENANT_UIDS.includes(scopeUid)) || MESSAGING_ENABLED;
}
